import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Lock, Bot, PlayCircle, History, AlertTriangle, ExternalLink,
} from "lucide-react";
import { formatDay } from "@/lib/format";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { useTerm } from "@/lib/terminology.jsx";

const DEFAULT_PROMPT = `You are a senior VC analyst. Given the deal summary provided, produce a well-structured markdown analysis. Focus on: (1) key strengths, (2) risks, and (3) recommended next steps. Be objective and cite assumptions. Keep it under 300 words.`;

/**
 * v6.1 Phase 1b — Agent Editor with BYO-aware provider/model dropdowns,
 * Configuration History, Recent Failures, and Test panel.
 *
 * Save flow:
 *  - Base fields (name/description/type/prompt/interaction) → /api/agents PATCH
 *  - provider_id + model_id  → /api/v61/agents/{id}/config PATCH (validates BYO)
 */
export default function AgentEditor() {
  const { agentId } = useParams();
  const nav = useNavigate();
  const isNew = !agentId;
  const { pipelineId } = usePipelineCtx();
  const term = useTerm();

  const [agent, setAgent] = useState({
    agent_name: "",
    description: "",
    agent_type: "research",
    provider_id: "",
    model_id: "",
    system_prompt: DEFAULT_PROMPT,
    interaction_mode: "single_shot",
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [credStatus, setCredStatus] = useState(null);
  const [configHistory, setConfigHistory] = useState([]);
  const [failures, setFailures] = useState([]);

  // Load providers list once
  useEffect(() => {
    api.get("/v61/providers").then(({ data }) => setProviders(data));
  }, []);

  // Load agent (edit mode)
  useEffect(() => {
    if (isNew) return;
    api.get(`/agents/${agentId}`).then(({ data }) => {
      setAgent((prev) => ({
        ...prev,
        ...data,
        // Prefer v61 fields; fall back to legacy on rows not yet backfilled
        provider_id: data.provider_id || "",
        model_id: data.model_id || data.model_name || "",
      }));
      setLoading(false);
    });
    api.get(`/v61/agents/${agentId}/config-history`).then(({ data }) => setConfigHistory(data));
    api.get(`/v61/agents/${agentId}/failures`).then(({ data }) => setFailures(data));
  }, [agentId, isNew]);

  // Load models when provider changes
  const loadModels = useCallback((pid) => {
    if (!pid) { setModels([]); setCredStatus(null); return; }
    api.get("/v61/models", { params: { provider_id: pid } }).then(({ data }) => {
      setModels(data.models || []);
      setCredStatus(data.credential_status);
    });
  }, []);
  useEffect(() => { loadModels(agent.provider_id); }, [agent.provider_id, loadModels]);

  const isPublished = agent.is_published;
  const set = (k, v) => setAgent((a) => ({ ...a, [k]: v }));
  const currentProvider = providers.find((p) => p.provider_id === agent.provider_id);

  const providerHasIssue = agent.provider_id && credStatus !== "valid";
  const providerIssueMsg = (() => {
    if (!agent.provider_id) return null;
    if (credStatus === null) return null;
    const name = currentProvider?.name || "this provider";
    if (credStatus === "no_credential")
      return `You haven't added a ${name} API key yet. Add one under Settings > AI & Model Configuration first, then try again.`;
    if (credStatus === "invalid")
      return `The ${name} API key on file is invalid. Fix it in Settings > AI & Model Configuration before assigning it to an ${term("Agent")}.`;
    if (credStatus === "quota_exceeded")
      return `${name}'s account behind this key is out of credit or hit a billing limit.`;
    if (credStatus === "unverified")
      return `The ${name} API key was saved without validation. Re-validate it under Settings > AI & Model Configuration.`;
    return null;
  })();

  const save = async () => {
    if (!agent.agent_name.trim() || !agent.system_prompt.trim()) {
      toast.error("Name and system prompt are required");
      return;
    }
    if (!agent.provider_id || !agent.model_id) {
      toast.error("Please choose a Provider and Model");
      return;
    }
    setSaving(true);
    try {
      const legacyPayload = {
        agent_name: agent.agent_name,
        description: agent.description,
        agent_type: agent.agent_type,
        system_prompt: agent.system_prompt,
        interaction_mode: agent.interaction_mode || "single_shot",
        // Also send legacy provider/model so /agents endpoint stays valid;
        // model_provider maps 'google' -> 'gemini' historically.
        model_provider: currentProvider?.provider_key === "google" ? "gemini" : currentProvider?.provider_key,
        model_name: agent.model_id,
      };

      if (isNew) {
        const { data } = await api.post("/agents", { ...legacyPayload, pipeline_id: pipelineId });
        // Follow up with the BYO validation PATCH
        try {
          await api.patch(`/v61/agents/${data.agent_id}/config`, {
            provider_id: agent.provider_id, model_id: agent.model_id,
          });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Provider validation failed");
          setSaving(false);
          return;
        }
        toast.success(`${term("Agent")} created`);
        nav(`/agents/${data.agent_id}`);
      } else if (isPublished) {
        await api.patch(`/agents/${agentId}/name`, {
          name: agent.agent_name, description: agent.description,
        });
        toast.success(`${term("Agent")} updated`);
      } else {
        await api.patch(`/agents/${agentId}`, legacyPayload);
        try {
          await api.patch(`/v61/agents/${agentId}/config`, {
            provider_id: agent.provider_id, model_id: agent.model_id,
          });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Provider validation failed");
          setSaving(false);
          return;
        }
        toast.success(`${term("Agent")} updated`);
        const { data } = await api.get(`/v61/agents/${agentId}/config-history`);
        setConfigHistory(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!window.confirm("Publishing locks this agent permanently. Duplicate later to edit. Confirm?")) return;
    try {
      await api.post(`/agents/${agentId}/publish`);
      toast.success(`${term("Agent")} published`);
      const { data } = await api.get(`/agents/${agentId}`);
      setAgent((p) => ({ ...p, ...data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="p-10">Loading…</div>;

  const inp = "w-full h-10 px-3 rounded-md border text-sm disabled:opacity-70";

  return (
    <div className="p-8 max-w-3xl">
      <button onClick={() => nav("/agents")}
        className="text-sm inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="back-to-agents">
        <ArrowLeft size={14} /> Back to {term("Agents")}
      </button>

      <div className="mt-4 flex justify-between items-start">
        <div>
          <div className="cw-caption uppercase tracking-widest">
            {isNew ? "New" : isPublished ? "Published" : "Draft"} {term("Agent")}
          </div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
            <Bot size={24} /> {isNew ? `Create ${term("Agent")}` : agent.agent_name}
          </h1>
        </div>
        {!isNew && !isPublished && (
          <button onClick={publish} className="cw-btn-primary inline-flex items-center gap-1"
            data-testid="publish-agent-btn">
            <Lock size={14} /> Publish
          </button>
        )}
      </div>

      {isPublished && (
        <div className="mt-4 p-3 rounded-md text-sm"
          style={{ background: "rgba(40,42,116,0.06)", color: "var(--cw-primary)" }}>
          This {term("agent").toLowerCase()} is published. Steps and scope are locked, but name and description can still be edited.
        </div>
      )}

      <div className="mt-6 space-y-4">
        <div className="cw-card p-5 space-y-4">
          <Field label={`${term("Agent")} Name *`}>
            <input value={agent.agent_name}
              onChange={(e) => set("agent_name", e.target.value)}
              className={inp} style={{ borderColor: "var(--cw-border)" }}
              data-testid="agent-name-input" />
          </Field>
          <Field label="Description">
            <textarea value={agent.description || ""} rows={2}
              onChange={(e) => set("description", e.target.value)}
              className="w-full px-3 py-2 rounded-md border text-sm disabled:opacity-70"
              style={{ borderColor: "var(--cw-border)" }} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Type">
              <select value={agent.agent_type} disabled={isPublished}
                onChange={(e) => set("agent_type", e.target.value)}
                className={inp + " bg-white"} style={{ borderColor: "var(--cw-border)" }}
                data-testid="agent-type-select">
                <option value="research">Research</option>
                <option value="scoring">Scoring</option>
                <option value="summary">Summary</option>
                <option value="custom">Custom</option>
              </select>
            </Field>
            <Field label="Provider">
              <select value={agent.provider_id} disabled={isPublished}
                onChange={(e) => { set("provider_id", e.target.value); set("model_id", ""); }}
                className={inp + " bg-white"} style={{ borderColor: providerHasIssue ? "var(--cw-error)" : "var(--cw-border)" }}
                data-testid="agent-provider-select">
                <option value="">Select…</option>
                {providers.map((p) => (
                  <option key={p.provider_id} value={p.provider_id}>{p.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Model">
              <select value={agent.model_id} disabled={isPublished || !agent.provider_id || models.length === 0}
                onChange={(e) => set("model_id", e.target.value)}
                className={inp + " bg-white"} style={{ borderColor: "var(--cw-border)" }}
                data-testid="agent-model-select">
                {agent.model_id && !models.includes(agent.model_id) && (
                  <option value={agent.model_id}>{`${agent.model_id} (not in catalogue)`}</option>
                )}
                {models.length === 0 && !agent.model_id && (
                  <option value="">— add a key first —</option>
                )}
                {models.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>
          </div>

          {providerIssueMsg && (
            <div
              className="text-xs px-3 py-2 rounded flex items-start gap-2"
              style={{ background: "rgba(215,60,60,0.08)", color: "#B02B2B" }}
              data-testid="agent-provider-issue"
            >
              <AlertTriangle size={12} className="mt-0.5" />
              <div className="flex-1">{providerIssueMsg}</div>
              <a
                href="/settings?sub=ai-keys"
                className="underline shrink-0 inline-flex items-center gap-1"
                data-testid="agent-provider-issue-link"
              >
                Go to Settings <ExternalLink size={10} />
              </a>
            </div>
          )}

          <div className="mt-4">
            <Field label="Interaction Mode">
              <select
                value={agent.interaction_mode || "single_shot"}
                disabled={isPublished}
                onChange={(e) => set("interaction_mode", e.target.value)}
                className={inp + " bg-white"}
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="agent-interaction-select"
              >
                <option value="single_shot">Single-shot (one output)</option>
                <option value="interactive">Interactive (multi-turn conversation)</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="cw-card p-5">
          <h3 className="cw-h3 mb-2">System Prompt</h3>
          <div className="text-xs text-[var(--cw-muted)] mb-2">
            Instructs the {term("agent").toLowerCase()} on its role. The {term("deal").toLowerCase()} context is auto-appended on each run.
          </div>
          <textarea value={agent.system_prompt} disabled={isPublished} rows={10}
            onChange={(e) => set("system_prompt", e.target.value)}
            className="w-full px-3 py-2 rounded-md border text-sm font-mono disabled:opacity-70"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="agent-prompt-input" />
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving}
            className="cw-btn-primary inline-flex items-center gap-2"
            data-testid="save-agent-btn">
            <Save size={14} /> {saving ? "Saving…" : (isNew ? `Create ${term("Agent")}` : (isPublished ? "Save Name / Description" : "Save Changes"))}
          </button>
        </div>

        {!isNew && (
          <TestPanel
            agentId={agentId}
            providerConnected={credStatus === "valid"}
            onFailureRecorded={() =>
              api.get(`/v61/agents/${agentId}/failures`).then(({ data }) => setFailures(data))
            }
          />
        )}

        {!isNew && (
          <ConfigHistory rows={configHistory} providers={providers} />
        )}

        {!isNew && (
          <RecentFailures rows={failures} />
        )}
      </div>
    </div>
  );
}

/* ---------------------------- Test Panel ---------------------------- */
function TestPanel({ agentId, providerConnected, onFailureRecorded }) {
  const term = useTerm();
  const [context, setContext] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    if (!context.trim()) { toast.error("Add some context first"); return; }
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const { data } = await api.post(`/v61/agents/${agentId}/test`, { prompt_context: context });
      if (data.ok) {
        setResult(data);
      } else {
        setError(data.failure_detail || "Test failed");
        onFailureRecorded?.();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Test failed");
      onFailureRecorded?.();
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="cw-card p-5" data-testid="agent-test-panel">
      <h3 className="cw-h3 mb-2 inline-flex items-center gap-2">
        <PlayCircle size={16} /> Test this {term("agent").toLowerCase()}
      </h3>
      <div className="text-xs text-[var(--cw-muted)] mb-2">
        Bring your own context — this will be appended to the system prompt and sent to the configured provider.
      </div>
      <textarea
        value={context}
        onChange={(e) => setContext(e.target.value)}
        rows={5}
        placeholder={`Paste example ${term("deal").toLowerCase()} context here to test the agent's response…`}
        className="w-full px-3 py-2 rounded-md border text-sm"
        style={{ borderColor: "var(--cw-border)" }}
        data-testid="agent-test-context"
      />
      <div className="mt-2 flex justify-end">
        <button
          onClick={run}
          disabled={running || !providerConnected}
          title={!providerConnected ? "Connect a valid API key first" : ""}
          className="cw-btn-primary text-sm inline-flex items-center gap-1"
          data-testid="agent-test-run"
        >
          <PlayCircle size={14} /> {running ? "Running…" : "Run test"}
        </button>
      </div>
      {result && (
        <div
          className="mt-3 text-sm p-3 rounded whitespace-pre-wrap"
          style={{ background: "rgba(44,154,82,0.06)" }}
          data-testid="agent-test-result"
        >
          <div className="text-xs text-[var(--cw-muted)] mb-1">
            Success · {result.response_time_ms}ms
            {result.tokens_input != null && ` · in ${result.tokens_input} tok`}
            {result.tokens_output != null && ` · out ${result.tokens_output} tok`}
          </div>
          {result.output_text}
        </div>
      )}
      {error && (
        <div
          className="mt-3 text-sm p-3 rounded flex items-start gap-2"
          style={{ background: "rgba(215,60,60,0.08)", color: "#B02B2B" }}
          data-testid="agent-test-error"
        >
          <AlertTriangle size={14} className="mt-0.5" /> {error}
        </div>
      )}
    </div>
  );
}

/* ---------------------------- Config History ---------------------------- */
function ConfigHistory({ rows, providers }) {
  const [open, setOpen] = useState(false);
  const providerName = (pid) =>
    providers.find((p) => p.provider_id === pid)?.name || pid;
  return (
    <div className="cw-card p-5" data-testid="agent-config-history">
      <button
        className="w-full text-left flex items-center justify-between"
        onClick={() => setOpen(!open)}
      >
        <h3 className="cw-h3 inline-flex items-center gap-2">
          <History size={16} /> Configuration History ({rows.length})
        </h3>
        <span className="text-xs text-[var(--cw-muted)]">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {rows.length === 0 && (
            <div className="text-sm text-[var(--cw-muted)]">No changes recorded yet.</div>
          )}
          {rows.map((r) => (
            <div
              key={r.log_id}
              className="border-l-2 pl-3 py-1"
              style={{ borderColor: "var(--cw-primary)" }}
              data-testid={`config-history-${r.log_id}`}
            >
              <div className="text-xs text-[var(--cw-muted)]">
                {r.actor_name} · {formatDay(r.created_at)} · {r.source}
              </div>
              {Object.entries(r.changes || {}).map(([field, val]) => (
                <div key={field} className="text-sm">
                  <strong>{field}</strong>:{" "}
                  <code className="text-xs">
                    {field === "provider_id" ? providerName(val.before) : String(val.before ?? "—")}
                  </code>
                  {" → "}
                  <code className="text-xs">
                    {field === "provider_id" ? providerName(val.after) : String(val.after ?? "—")}
                  </code>
                </div>
              ))}
              {r.source === "bulk_remap" && r.bulk_remap_id && (
                <a
                  className="text-[11px] text-[var(--cw-primary)] hover:underline mt-1 inline-block"
                  href={`/bulk-remap?remap=${r.bulk_remap_id}`}
                  data-testid={`view-full-remap-${r.log_id}`}
                >
                  View full remap →
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------- Recent Failures ---------------------------- */
function RecentFailures({ rows }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="cw-card p-5" data-testid="agent-recent-failures">
      <button
        className="w-full text-left flex items-center justify-between"
        onClick={() => setOpen(!open)}
      >
        <h3 className="cw-h3 inline-flex items-center gap-2">
          <AlertTriangle size={16} className={rows.length ? "text-[#B02B2B]" : ""} />
          Recent Failures ({rows.length})
        </h3>
        <span className="text-xs text-[var(--cw-muted)]">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {rows.length === 0 && (
            <div className="text-sm text-[var(--cw-muted)]">
              No recent failures. Everything's healthy.
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.log_id}
              className="p-3 rounded"
              style={{ background: "rgba(215,60,60,0.05)" }}
              data-testid={`failure-${r.log_id}`}
            >
              <div className="text-xs text-[var(--cw-muted)]">
                {formatDay(r.created_at)} · {r.error_category}
                {r.provider_error_code && ` · ${r.provider_error_code}`}
                {r.invocation_context && ` · ${r.invocation_context}`}
              </div>
              <div className="text-sm mt-1">{r.failure_detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="cw-caption block mb-1">{label}</label>
      {children}
    </div>
  );
}
